import {Record, List} from 'immutable';
import moment from 'moment-timezone';
import {type Moment} from 'moment-timezone';

interface IPlanPartState {
    readonly monthlyEstimate: number;
    readonly weeklyTotal: number;
    readonly planPartId: string;
    readonly pricingGroupId: string;
}

export class PlanPartState extends Record<IPlanPartState>({
    monthlyEstimate: 0,
    weeklyTotal: 0,
    planPartId: '',
    pricingGroupId: '',
}) {}

interface IFundedBooking {
    readonly bookingId: string;
    readonly fundedMinutes: number | null;
}

class FundedBooking extends Record<IFundedBooking>({
    bookingId: '',
    fundedMinutes: null,
}) {}

interface IPublicFunding {
    readonly amount: number;
    readonly claimable: number;
    readonly applied: number;
    readonly hours: number;
    readonly minutes: number | null;
    readonly period: string;
    readonly fundedBookings: List<FundedBooking>;
}

export class PublicFundingState extends Record<IPublicFunding>({
    amount: 0,
    claimable: 0,
    applied: 0,
    hours: 0,
    minutes: null,
    period: '',
    fundedBookings: List(),
}) {}

interface IPlanState {
    readonly from?: Moment;
    readonly to?: Moment;
    readonly ageGroupId: string;
    readonly pricingGroupId: string;
    readonly monthlyEstimate: number;
    readonly planPartStates: List<PlanPartState>;
    readonly publicFunding: PublicFundingState;
}

export class PlanState extends Record<IPlanState>({
    from: undefined,
    to: undefined,
    ageGroupId: '',
    pricingGroupId: '',
    monthlyEstimate: 0,
    planPartStates: List(),
    publicFunding: new PublicFundingState(),
}) {}

const fundedBookingFromApi = (booking: any) => new FundedBooking(booking);

const publicFundingFromApi = (publicFunding: any) =>
    new PublicFundingState({
        ...publicFunding,
        fundedBookings: List(publicFunding.fundedBookings).map(fundedBookingFromApi),
    });

const planPartStateFromApi = (planPartState: any) => new PlanPartState(planPartState);

const fromAPI = (planState: any) =>
    new PlanState({
        ...planState,
        to: planState.to ? moment(planState.to, 'YYYY-MM-DD') : undefined,
        from: planState.from ? moment(planState.from, 'YYYY-MM-DD') : undefined,
        planPartStates: List(planState.planPartStates).map(planPartStateFromApi),
        publicFunding: publicFundingFromApi(planState.publicFunding),
    });

export default {
    record: PlanState,
    default: new PlanState(),
    fromAPI,
};
